import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getReport, getTransactions, getWithdrawals, getSalaries, getSBO, getCards } from '../api';
import {
  formatNumber,
  convertDateForApi,
  formatDateForDisplay,
  getTodayByLocale,
  getMonthStartByLocale,
  getDateConfig,
  switchDateLocale,
  usePagination,
  calculateUsdEquivalent,
} from '../utils';
import DatePicker from 'react-multi-date-picker';

function Reports({ dateLocale }) {
  const dateConfig = getDateConfig(dateLocale);

  const [report, setReport] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [sboPayments, setSboPayments] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(getMonthStartByLocale(dateLocale));
  const [endDate, setEndDate] = useState(getTodayByLocale(dateLocale));
  const [transactionFilter, setTransactionFilter] = useState({ card_id: '' });
  const prevLocaleRef = useRef(dateLocale);
  const transactionsPagination = usePagination(transactions);
  const withdrawalsPagination = usePagination(withdrawals);
  const sboPagination = usePagination(sboPayments);
  const salariesPagination = usePagination(salaries);
  const withdrawalTotalsByManager = useMemo(() => {
    if (!Array.isArray(withdrawals) || withdrawals.length === 0) {
      return [];
    }

    const totals = withdrawals.reduce((accumulator, item) => {
      const usdValue = calculateUsdEquivalent(item);

      if (usdValue === null) {
        return accumulator;
      }

      const managerKey =
        item.manager_name && item.manager_name.trim() !== '' ? item.manager_name : 'نامشخص';

      const currentTotal = accumulator.get(managerKey) || 0;
      accumulator.set(managerKey, currentTotal + usdValue);
      return accumulator;
    }, new Map());

    return Array.from(totals.entries())
      .map(([manager, total]) => ({
        manager,
        total: parseFloat(total.toFixed(2)),
      }))
      .sort((a, b) => b.total - a.total);
  }, [withdrawals]);

  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(allTransactions) || allTransactions.length === 0) {
      return [];
    }
    if (!transactionFilter.card_id) {
      return allTransactions;
    }
    return allTransactions.filter((t) => {
      if (t.card_id) {
        return t.card_id.toString() === transactionFilter.card_id.toString();
      }
      return false;
    });
  }, [allTransactions, transactionFilter]);

  useEffect(() => {
    setTransactions(filteredTransactions);
  }, [filteredTransactions]);

  const transactionsTotal = useMemo(() => {
    return filteredTransactions.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0);
    }, 0);
  }, [filteredTransactions]);

  const cardTotals = useMemo(() => {
    if (!Array.isArray(allTransactions) || allTransactions.length === 0) {
      return [];
    }

    const cardData = allTransactions.reduce((accumulator, item) => {
      const cardKey = item.card_number && item.card_number.trim() !== '' ? item.card_number : 'نامشخص';
      const amount = parseFloat(item.amount) || 0;
      const personName = item.person_name && item.person_name.trim() !== '' ? item.person_name : null;
      
      if (!accumulator.has(cardKey)) {
        accumulator.set(cardKey, {
          total: 0,
          persons: new Set(),
        });
      }
      
      const cardInfo = accumulator.get(cardKey);
      cardInfo.total += amount;
      if (personName) {
        cardInfo.persons.add(personName);
      }
      
      return accumulator;
    }, new Map());

    return Array.from(cardData.entries())
      .map(([card, data]) => ({
        card,
        total: parseFloat(data.total.toFixed(2)),
        persons: Array.from(data.persons),
      }))
      .sort((a, b) => b.total - a.total);
  }, [allTransactions]);

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const prevLocale = prevLocaleRef.current;
    if (prevLocale !== dateLocale) {
      setStartDate((prev) => switchDateLocale(prev, prevLocale, dateLocale) || getMonthStartByLocale(dateLocale));
      setEndDate((prev) => switchDateLocale(prev, prevLocale, dateLocale) || getTodayByLocale(dateLocale));
      prevLocaleRef.current = dateLocale;
    }
  }, [dateLocale]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const startDateGregorian = convertDateForApi(startDate, dateLocale);
      const endDateGregorian = convertDateForApi(endDate, dateLocale);
      const [reportRes, transactionsRes, withdrawalsRes, salariesRes, sboRes, cardsRes] = await Promise.all([
        getReport(startDateGregorian, endDateGregorian),
        getTransactions({ start_date: startDateGregorian, end_date: endDateGregorian }),
        getWithdrawals({ start_date: startDateGregorian, end_date: endDateGregorian }),
        getSalaries({ start_date: startDateGregorian, end_date: endDateGregorian }),
        getSBO({ start_date: startDateGregorian, end_date: endDateGregorian }),
        getCards(),
      ]);
      setReport(reportRes.data);
      setAllTransactions(transactionsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setSalaries(salariesRes.data);
      setSboPayments(sboRes.data);
      setCards(cardsRes.data);
      setError('');
    } catch (err) {
      setError('خطا در بارگذاری گزارش');
    } finally {
      setLoading(false);
    }
  }, [dateLocale, endDate, startDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  const summaryCards = report
    ? [
        {
          id: 'income',
          title: 'درآمد ریالی',
          value: `${formatNumber(report.income)} تومان`,
          tone: 'success',
          caption: 'جمع تراکنش‌های ریالی ورودی',
        },
        {
          id: 'withdrawals-irr',
          title: 'برداشت‌های ریالی',
          value: `${formatNumber(report.withdrawals)} تومان`,
          tone: 'danger',
          caption: 'خروجی‌های تومانی',
        },
        {
          id: 'balance',
          title: 'موجودی خالص',
          value: `${formatNumber(report.balance)} تومان`,
          tone: report.balance >= 0 ? 'success' : 'danger',
          caption: 'وضعیت پس از اعمال برداشت‌ها',
        },
        {
          id: 'sbo-payments',
          title: 'پرداخت‌های دلاری',
          value: `${formatNumber(report.sbo_payments)} دلار`,
          tone: 'primary',
          caption: 'دریافتی‌های دلاری',
        },
 
       
        {
          id: 'withdrawals-usd',
          title: 'برداشت‌های دلاری',
          value: `${formatNumber(report.withdrawals_usd)} دلار`,
          tone: 'warning',
          caption: 'خروجی‌های دلاری مستقیم',
        },
        {
          id: 'withdrawals-usd-from-irr',
          title: "مانده دلاری",
          value: `${formatNumber(report.sbo_payments - report.withdrawals_usd)}  `,
          tone: 'warning',
          caption: 'مانده دلاری',
        },

        {
          id: 'withdrawals-usd-from-irr',
          title: 'معادل دلاری برداشت‌های ریالی',
          value: `${formatNumber(report.withdrawals_usd_equivalent_from_irr)}  `,
          tone: 'warning',
          caption: 'محاسبه‌شده بر اساس نرخ تبدیل',
        },
        {
          id: 'withdrawals-usd-total',
          title: 'جمع خروج دلاری',
          value: `${formatNumber(report.withdrawals_usd_total_equivalent)} دلار`,
          tone: 'primary',
          caption: 'مجموع دلار مستقیم و معادل',
        },
       
        {
          id: 'salaries',
          title: 'حقوق پرداختی',
          value: `${formatNumber(report.salaries)} تومان`,
          tone: 'danger',
          caption: 'جمع حقوق ثبت‌شده',
        },
        
      ]
    : [];

  const toneToTextClass = {
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    primary: 'text-primary',
  };

  return (
    <div className="d-grid gap-4">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0">
          <h2 className="h5 mb-1">فیلتر بازه زمانی گزارش</h2>
          <p className="text-muted small mb-0">بازه مدنظر را انتخاب کنید تا آمار به‌روزرسانی شود</p>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">از تاریخ</label>
              <DatePicker
                value={startDate}
                onChange={(date) => {
                  if (date) {
                    setStartDate(date);
                  }
                }}
                calendar={dateConfig.calendar}
                locale={dateConfig.locale}
                calendarPosition="bottom-right"
                inputClass="form-control"
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">تا تاریخ</label>
              <DatePicker
                value={endDate}
                onChange={(date) => {
                  if (date) {
                    setEndDate(date);
                  }
                }}
                calendar={dateConfig.calendar}
                locale={dateConfig.locale}
                calendarPosition="bottom-right"
                inputClass="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      {report && (
        <div className="row g-3">
          {summaryCards.map((item) => (
            <div className="col-12 col-sm-6 col-xl-4" key={item.id}>
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <span className="badge bg-light text-dark mb-3">{item.title}</span>
                  <div className={`fs-4 fw-bold ${toneToTextClass[item.tone] || 'text-primary'}`}>
                    {item.value}
                  </div>
                  <p className="text-muted small mb-0 mt-2">{item.caption}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h5 mb-1">جمع برداشت‌های دلاری بر اساس مدیر</h2>
            <p className="text-muted small mb-0">جمع‌بندی دلار مستقیم و معادل در بازه انتخابی</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">
            {withdrawalTotalsByManager.length} مدیر
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">ردیف</th>
                  <th scope="col">مدیر</th>
                  <th scope="col">جمع برداشت دلاری</th>
                </tr>
              </thead>
              <tbody>
                {withdrawalTotalsByManager.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center text-muted py-4">
                      داده‌ای برای نمایش وجود ندارد
                    </td>
                  </tr>
                )}
                {withdrawalTotalsByManager.map((item, index) => (
                  <tr key={item.manager}>
                    <td>
                      <span className="badge bg-secondary">{index + 1}</span>
                    </td>
                    <td>{item.manager}</td>
                    <td>
                      <div className="fw-bold text-dark">{formatNumber(item.total)}</div>
                      <small className="text-muted">USD</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h5 mb-1">تراکنش‌ها</h2>
            <p className="text-muted small mb-0">فهرست تراکنش‌های ثبت‌شده در بازه انتخابی</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">
            {transactionsPagination.totalItems} مورد
          </span>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <label className="form-label">فیلتر بر اساس کارت</label>
              <select
                className="form-select"
                value={transactionFilter.card_id}
                onChange={(e) =>
                  setTransactionFilter((prev) => ({
                    ...prev,
                    card_id: e.target.value,
                  }))
                }
              >
                <option value="">همه کارت‌ها</option>
                {cards.map((card) => (
                  <option key={card.id} value={card.id}>
                    {card.card_number} - {card.bank_name} ({card.person_name})
                  </option>
                ))}
              </select>
            </div>
            {transactionFilter.card_id && (
              <div className="col-12 col-md-2 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={() => setTransactionFilter({ card_id: '' })}
                >
                  حذف فیلتر
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">ردیف</th>
                  <th scope="col">کارت</th>
                  <th scope="col">شخص</th>
                  <th scope="col">مبلغ</th>
                  <th scope="col">تاریخ</th>
                </tr>
              </thead>
              <tbody>
                {transactionsPagination.totalItems === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      تراکنشی در این بازه ثبت نشده است
                    </td>
                  </tr>
                )}
                {transactionsPagination.paginatedItems.map((transaction, index) => (
                  <tr key={transaction.id}>
                    <td>
                      <span className="badge bg-secondary">{transactionsPagination.rangeStart + index}</span>
                    </td>
                    <td>{transaction.card_number}</td>
                    <td>{transaction.person_name}</td>
                    <td>
                      <div className="fw-bold text-dark">{formatNumber(transaction.amount)}</div>
                      <small className="text-muted">تومان</small>
                    </td>
                    <td>{formatDateForDisplay(transaction.transaction_date, dateLocale)}</td>
                  </tr>
                ))}
              </tbody>
              {transactionsPagination.totalItems > 0 && (
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="3" className="text-end fw-bold">
                      جمع کل:
                    </td>
                    <td>
                      <div className="fw-bold text-success">{formatNumber(transactionsTotal)}</div>
                      <small className="text-muted">تومان</small>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {renderPaginationControls(transactionsPagination)}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h5 mb-1">جمع تراکنش‌ها بر اساس کارت</h2>
            <p className="text-muted small mb-0">مجموع مبالغ هر کارت در بازه انتخابی</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">
            {cardTotals.length} کارت
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">ردیف</th>
                  <th scope="col">کارت</th>
                  <th scope="col">شخص</th>
                  <th scope="col">جمع مبلغ</th>
                </tr>
              </thead>
              <tbody>
                {cardTotals.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4">
                      داده‌ای برای نمایش وجود ندارد
                    </td>
                  </tr>
                )}
                {cardTotals.map((item, index) => (
                  <tr key={item.card}>
                    <td>
                      <span className="badge bg-secondary">{index + 1}</span>
                    </td>
                    <td>{item.card}</td>
                    <td>
                      {item.persons.length > 0 ? (
                        item.persons.length === 1 ? (
                          item.persons[0]
                        ) : (
                          <span title={item.persons.join(', ')}>
                            {item.persons.join(', ')}
                          </span>
                        )
                      ) : (
                        <span className="text-muted">نامشخص</span>
                      )}
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{formatNumber(item.total)}</div>
                      <small className="text-muted">تومان</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h5 mb-1">برداشت‌ها</h2>
            <p className="text-muted small mb-0">جزئیات برداشت‌های ریالی و دلاری</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">
            {withdrawalsPagination.totalItems} مورد
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">ردیف</th>
                  <th scope="col">مدیر</th>
                  <th scope="col">نوع ارز</th>
                  <th scope="col">مبلغ</th>
                  <th scope="col">نرخ ارز</th>
                  <th scope="col">معادل دلار</th>
                  <th scope="col">تاریخ</th>
                  <th scope="col">توضیحات</th>
                </tr>
              </thead>
              <tbody>
                {withdrawalsPagination.totalItems === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      برداشتی ثبت نشده است
                    </td>
                  </tr>
                )}
                {withdrawalsPagination.paginatedItems.map((withdrawal, index) => {
                  const hasAmount = withdrawal.amount !== null && withdrawal.amount !== undefined;
                  const amountValue = hasAmount ? parseFloat(withdrawal.amount) : null;
                  const exchangeRateValue =
                    withdrawal.exchange_rate !== null && withdrawal.exchange_rate !== undefined
                      ? parseFloat(withdrawal.exchange_rate)
                      : null;
                  const isValidAmount = amountValue !== null && !Number.isNaN(amountValue);
                  const isValidRate =
                    exchangeRateValue !== null && !Number.isNaN(exchangeRateValue) && exchangeRateValue > 0;
                  const usdEquivalent = calculateUsdEquivalent(withdrawal);

                  return (
                    <tr key={withdrawal.id}>
                      <td>
                        <span className="badge bg-secondary">{withdrawalsPagination.rangeStart + index}</span>
                      </td>
                      <td>{withdrawal.manager_name}</td>
                      <td>{withdrawal.currency === 'USD' ? 'دلار' : 'ریال'}</td>
                      <td>
                        <div className="fw-bold text-dark">
                          {formatNumber(isValidAmount ? amountValue : withdrawal.amount)}
                        </div>
                        <small className="text-muted">
                          {withdrawal.currency === 'USD' ? 'دلار' : 'تومان'}
                        </small>
                      </td>
                      <td>
                        {withdrawal.currency === 'USD' || !isValidRate
                          ? '-'
                          : `${formatNumber(exchangeRateValue)} تومان`}
                      </td>
                      <td>
                        {usdEquivalent === null ? (
                          '-'
                        ) : (
                          <>
                            <div className="fw-bold text-dark">{formatNumber(usdEquivalent)}</div>
                            <small className="text-muted">USD</small>
                          </>
                        )}
                      </td>
                      <td>{formatDateForDisplay(withdrawal.withdrawal_date, dateLocale)}</td>
                      <td>{withdrawal.description || <span className="text-muted">بدون توضیح</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {renderPaginationControls(withdrawalsPagination)}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h5 mb-1">پرداخت‌های دلاری</h2>
            <p className="text-muted small mb-0">ریز پرداخت‌های ثبت‌شده دلاری</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">
            {sboPagination.totalItems} مورد
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">ردیف</th>
                  <th scope="col">نام مشتری</th>
                  <th scope="col">مبلغ (دلار)</th>
                  <th scope="col">تاریخ</th>
                  <th scope="col">توضیحات</th>
                </tr>
              </thead>
              <tbody>
                {sboPagination.totalItems === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      پرداختی ثبت نشده است
                    </td>
                  </tr>
                )}
                {sboPagination.paginatedItems.map((payment, index) => (
                  <tr key={payment.id}>
                    <td>
                      <span className="badge bg-secondary">{sboPagination.rangeStart + index}</span>
                    </td>
                    <td>{payment.customer_name}</td>
                    <td>
                      <div className="fw-bold text-dark">{formatNumber(payment.amount)}</div>
                      <small className="text-muted">USD</small>
                    </td>
                    <td>{formatDateForDisplay(payment.payment_date, dateLocale)}</td>
                    <td>{payment.description || <span className="text-muted">بدون توضیح</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPaginationControls(sboPagination)}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h5 mb-1">حقوق‌ها</h2>
            <p className="text-muted small mb-0">پرداخت‌های حقوقی ثبت‌شده</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">
            {salariesPagination.totalItems} مورد
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">ردیف</th>
                  <th scope="col">مبلغ</th>
                  <th scope="col">تاریخ</th>
                  <th scope="col">توضیحات</th>
                </tr>
              </thead>
              <tbody>
                {salariesPagination.totalItems === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4">
                      حقوقی ثبت نشده است
                    </td>
                  </tr>
                )}
                {salariesPagination.paginatedItems.map((salary, index) => (
                  <tr key={salary.id}>
                    <td>
                      <span className="badge bg-secondary">{salariesPagination.rangeStart + index}</span>
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{formatNumber(salary.amount)}</div>
                      <small className="text-muted">تومان</small>
                    </td>
                    <td>{formatDateForDisplay(salary.salary_date, dateLocale)}</td>
                    <td>{salary.description || <span className="text-muted">بدون توضیح</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPaginationControls(salariesPagination)}
        </div>
      </div>
    </div>
  );
}

export default Reports;

